import { AsyncPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, DOCUMENT, inject, OnInit } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { ActivatedRoute, Router } from '@angular/router';
import { provideTranslocoScope, TranslocoModule } from '@jsverse/transloco';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { nonNullable } from '@rma/generic/rxjs/operators/non-nullable';
import { rmaShareReplay } from '@rma/generic/rxjs/operators/rma-share-replay';
import { RmaIconComponent } from '@rma/generic/ui/icon';
import { CurrentUser, PublicUserContextService } from '@rma/public/accounts/user';
import { StaticImageUrlPipe } from '@rma/public/generic/util/environment';
import { TokenProviderService } from '@rma/public/generic/util/http';
import { map, Observable, of, ReplaySubject, startWith, Subject, switchMap, take } from 'rxjs';
import { MemberInvitationService } from '../data-access-member-invitation/member-invitation.service';
import { ProfileMemberInviteDetails } from '../domain/model/member-invitation.model';

@UntilDestroy()
@Component({
  selector: 'rma-accept-member-invitation',
  templateUrl: './accept-member-invitation.component.html',
  styleUrls: ['./accept-member-invitation.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [AsyncPipe, MatProgressSpinnerModule, MatButtonModule, TranslocoModule, RmaIconComponent, StaticImageUrlPipe],
  providers: [provideTranslocoScope('memberInvitation')],
})
export class AcceptMemberInvitationComponent implements OnInit {
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly document = inject<Document>(DOCUMENT);
  private readonly memberInvitationService = inject(MemberInvitationService);
  private readonly router = inject(Router);
  private readonly tokenProviderService = inject(TokenProviderService);
  private readonly userService = inject(PublicUserContextService);

  public profileMemberInviteDetails$: Observable<ProfileMemberInviteDetails | null>;
  public activationCode$: Observable<string | undefined>;
  public error$: Observable<string>;
  public pending$: Observable<boolean>;

  private error = new ReplaySubject<string>();
  private pending = new Subject<boolean>();

  constructor() {
    this.error$ = this.error.asObservable();
    this.pending$ = this.pending.asObservable().pipe(startWith(false));

    this.activationCode$ = this.activatedRoute.params.pipe(
      take(1),
      map(({ activationCode }) => activationCode),
      rmaShareReplay(),
    );

    this.profileMemberInviteDetails$ = this.activatedRoute.data.pipe(
      // SAFETY(legacy): unreviewed, pre-dates this rule.
      map(({ profileMemberInviteDetails }) => profileMemberInviteDetails as ProfileMemberInviteDetails | null),
    );
  }

  ngOnInit(): void {
    this.activationCode$.pipe(take(1)).subscribe((activationCode) => {
      if (!activationCode) {
        this.router.navigate(['/']);
      }
    });

    this.profileMemberInviteDetails$.pipe(take(1), untilDestroyed(this)).subscribe((profileMemberInviteDetails) => {
      if (!profileMemberInviteDetails) {
        this.error.next('An error occurred');
      }
      if (
        profileMemberInviteDetails?.activationStatusType === 'PendingAuthorisation' ||
        profileMemberInviteDetails?.activationStatusType === 'Deleted'
      ) {
        this.error.next("Invite can't be accepted");
      }

      if (profileMemberInviteDetails?.activationStatusType === 'Activated') {
        this.document.location.href = '/dashboard';
      }
    });
  }

  public acceptInvite() {
    this.pending.next(true);
    this.activationCode$
      .pipe(
        nonNullable(),
        switchMap((token) => this.memberInvitationService.acceptInvite(token, true)),
        switchMap((result) => (result.userToken ? this.persistJwtAuth(result.userToken) : of(null))),
        untilDestroyed(this),
      )
      .subscribe({
        next: (_) => {
          this.pending.next(false);

          this.document.location.href = '/dashboard';
        },
        error: (error) => {
          this.pending.next(false);
          this.error.next(error?.error?.message ?? error.message);
        },
      });
  }

  private persistJwtAuth(userToken: string): Observable<CurrentUser | null> {
    this.tokenProviderService.setUserToken(userToken);
    return this.userService.reloadUser();
  }
}
